import { aesGcmDecrypt, aesGcmEncrypt } from '@/stores/encryption/aesHelperWeb';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Crypto from 'expo-crypto';
import { CryptoEncoding } from 'expo-crypto';
import * as SecureStore from 'expo-secure-store';
import localforage from 'localforage';
import { Platform } from 'react-native';

const DEVICE_TOKEN_KEY = 'D_T_K';
const ENCRYPTION_KEY_STORAGE = 'E_K';

const generateRandomEncryptionKey = async (): Promise<string> => {
  let encryptionKeyValue: string;
  // generating 64 cryptographic strong random bytes
  const randomBytes = Crypto.getRandomBytes(64);
  // convert those bytes to String
  encryptionKeyValue = String.fromCharCode.apply(null, randomBytes);
  encryptionKeyValue = await Crypto.digestStringAsync(
    Crypto.CryptoDigestAlgorithm.SHA256,
    encryptionKeyValue,
    { encoding: CryptoEncoding.BASE64 },
  );
  return encryptionKeyValue;
};

const getOrCreateEncryptionKeyToEncryptTokenOnWeb =
  async (): Promise<string> => {
    let encryptionKey = await AsyncStorage.getItem(ENCRYPTION_KEY_STORAGE);
    if (!encryptionKey) {
      encryptionKey = await generateRandomEncryptionKey();
      await AsyncStorage.setItem(ENCRYPTION_KEY_STORAGE, encryptionKey);
    }
    return encryptionKey;
  };

const getOrCreateDeviceToken = async (): Promise<string> => {
  if (Platform.OS === 'ios' || Platform.OS === 'android') {
    let token = await SecureStore.getItemAsync(DEVICE_TOKEN_KEY);
    if (!token) {
      token = await generateRandomEncryptionKey();
      await SecureStore.setItemAsync(DEVICE_TOKEN_KEY, token);
    }
    return token;
  } else {
    // Web implementation
    const encryptionKey = await getOrCreateEncryptionKeyToEncryptTokenOnWeb();

    let encryptedToken = await localforage.getItem<string>(DEVICE_TOKEN_KEY);
    if (!encryptedToken) {
      const token = await generateRandomEncryptionKey();
      encryptedToken = await aesGcmEncrypt(
        token,
        encryptionKey,
        DEVICE_TOKEN_KEY,
      );
      await localforage.setItem(DEVICE_TOKEN_KEY, encryptedToken);
    }

    try {
      return await aesGcmDecrypt(
        encryptedToken,
        encryptionKey,
        DEVICE_TOKEN_KEY,
      );
    } catch (error) {
      // if decryption fails, remove the stored token and encryption key and intialization vector
      await localforage.removeItem(DEVICE_TOKEN_KEY);
      await AsyncStorage.removeItem(ENCRYPTION_KEY_STORAGE);
      // remove IV from local storage, its stored as x-D_T_K
      await AsyncStorage.removeItem('x-' + DEVICE_TOKEN_KEY);
      // generate new encryption key and token
      const token = await generateRandomEncryptionKey();
      const encryptionKey = await getOrCreateEncryptionKeyToEncryptTokenOnWeb();
      encryptedToken = await aesGcmEncrypt(
        token,
        encryptionKey,
        DEVICE_TOKEN_KEY,
      );
      await localforage.setItem(DEVICE_TOKEN_KEY, encryptedToken);
      return token;
    }
  }
};

let storedEncryptionKey: string | null = null;
let storedIV: string | null = null;
let storedEncryptedToken: string | null = null;

const loadDeviceTokenDataWeb = async (): Promise<void> => {
  if (Platform.OS === 'web') {
    storedEncryptionKey = await AsyncStorage.getItem(ENCRYPTION_KEY_STORAGE);
    storedIV = await AsyncStorage.getItem(`x-${DEVICE_TOKEN_KEY}`);
    storedEncryptedToken = await localforage.getItem<string>(DEVICE_TOKEN_KEY);
  }
};

const saveDeviceTokenDataWeb = async (): Promise<void> => {
  if (storedEncryptionKey) {
    await AsyncStorage.setItem(ENCRYPTION_KEY_STORAGE, storedEncryptionKey);
  }
  if (storedIV) {
    await AsyncStorage.setItem(`x-${DEVICE_TOKEN_KEY}`, storedIV);
  }
  if (storedEncryptedToken) {
    await localforage.setItem(DEVICE_TOKEN_KEY, storedEncryptedToken);
  }
  storedEncryptedToken = null;
  storedEncryptionKey = null;
  storedIV = null;
};

export {
  DEVICE_TOKEN_KEY,
  getOrCreateDeviceToken,
  loadDeviceTokenDataWeb,
  saveDeviceTokenDataWeb,
};
